import React, { useState, useEffect, useContext, useRef } from "react";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import { baseURL } from "../../utils/api";

const PriceListManager = () => {
    const { token } = useContext(AuthContext);
    const toast = useRef(null);
    const [materials, setMaterials] = useState([]);
    const [kontrahenci, setKontrahenci] = useState([]);
    const [przeznaczenia, setPrzeznaczenia] = useState([]);
    const [groupedData, setGroupedData] = useState([]);
    const [selectedMaterial, setSelectedMaterial] = useState(null);
    const [selectedKontrahent, setSelectedKontrahent] = useState(null);
    const [selectedPrzeznaczenie, setSelectedPrzeznaczenie] = useState(null);
    const [price, setPrice] = useState(null);
    const [expandedRows, setExpandedRows] = useState(null);
    const [editingRow, setEditingRow] = useState(null);
    const [editedPrice, setEditedPrice] = useState(null);

    useEffect(() => {
        fetchAll();
    }, []);

    const fetchAll = async () => {
        try {
            const [cennikRes, matRes, konRes, przRes] = await Promise.all([
                axios.get(`${baseURL}api/cennik`, {
                    headers: { Authorization: `Bearer ${token}` },
                }),
                axios.get(`${baseURL}api/materialy`, {
                    headers: { Authorization: `Bearer ${token}` },
                }),
                axios.get(`${baseURL}api/kontrahenci`, {
                    headers: { Authorization: `Bearer ${token}` },
                }),
                axios.get(`${baseURL}api/przeznaczenia`, {
                    headers: { Authorization: `Bearer ${token}` },
                }),
            ]);

            const grouped = {};
            cennikRes.data.forEach((row) => {
                const kontrahentId = row.kontrahentID;
                if (!grouped[kontrahentId]) {
                    grouped[kontrahentId] = {
                        kontrahent: {
                            id: row.kontrahentID,
                            nazwa: row.kontrahent,
                            adres: row.adres,
                            nip: row.nip,
                            email: row.email,
                        },
                        pozycje: [],
                    };
                }

                grouped[kontrahentId].pozycje.push({
                    id: row.ID,
                    material: row.material,
                    materialID: row.materialID,
                    cena: row.cena,
                    przeznaczenieID: row.przeznaczenieID,
                    przeznaczenie: row.przeznaczenie,
                });
            });

            setGroupedData(Object.values(grouped));
            setMaterials(matRes.data);
            setKontrahenci(konRes.data);
            setPrzeznaczenia(przRes.data.filter((p) => p.dostepny));
        } catch (err) {
            console.error("Błąd pobierania danych:", err);
        }
    };

    const updateEntry = (id) => {
        axios
            .put(`${baseURL}api/cenniki/${id}`, { cena: editedPrice }, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then(() => {
                toast.current.show({
                    severity: "success",
                    summary: "Zaktualizowano",
                    detail: "Cena została zaktualizowana",
                    life: 3000,
                });
                setEditingRow(null);
                fetchAll();
            })
            .catch((err) => {
                console.error("Błąd aktualizacji:", err);
            });
    };
    const handleAdd = () => {
        const przeznaczenieID = selectedPrzeznaczenie?.id ?? null;

        // Dedup z uwzględnieniem przeznaczenia: ta sama para materiał+kontrahent
        // może mieć osobne ceny per przeznaczenie oraz jedną cenę ogólną (NULL).
        const alreadyExists = groupedData.some(group =>
            group.kontrahent.id === selectedKontrahent.id &&
            group.pozycje.some(p =>
                p.materialID === selectedMaterial.id &&
                (p.przeznaczenieID ?? null) === przeznaczenieID
            )
        );

        if (alreadyExists) {
            toast.current.show({
                severity: "warn",
                summary: "Istnieje",
                detail: przeznaczenieID
                    ? "Cena dla tego materiału i przeznaczenia już istnieje"
                    : "Cena ogólna dla tego materiału już istnieje",
                life: 3000,
            });
            return;
        }

        const newEntry = {
            kontrahentID: selectedKontrahent.id,
            materialID: selectedMaterial.id,
            cena: price,
            przeznaczenieID,
        };

        axios
            .post(`${baseURL}api/cennikiAdd`, newEntry, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then(() => {
                toast.current.show({
                    severity: "success",
                    summary: "Dodano",
                    detail: "Cennik dodany pomyślnie",
                    life: 3000,
                });
                fetchAll();
                setSelectedMaterial(null);
                setSelectedKontrahent(null);
                setSelectedPrzeznaczenie(null);
                setPrice(null);
            })
            .catch((err) => {
                console.error("Błąd dodawania cennika:", err);
                toast.current.show({
                    severity: "error",
                    summary: "Błąd",
                    detail: "Nie udało się dodać cennika",
                    life: 3000,
                });
            });
    };

    const deleteEntry = (id) => {
        axios
            .delete(`${baseURL}api/cenniki/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then(() => {
                toast.current.show({
                    severity: "info",
                    summary: "Usunięto",
                    detail: "Pozycja cennika została usunięta",
                    life: 3000,
                });
                fetchAll();
            })
            .catch((err) => {
                console.error("Błąd usuwania:", err);
            });
    };

    const rowExpansionTemplate = (rowData) => {
        return (
            <DataTable value={rowData.pozycje} responsiveLayout="scroll" sortMode="multiple" removableSort>
                <Column field="material" header="Materiał" sortable />
                <Column
                    header="Przeznaczenie"
                    sortable
                    sortField="przeznaczenie"
                    body={(row) =>
                        row.przeznaczenie ? (
                            <span className="cennik-przezn cennik-przezn--named">
                                {row.przeznaczenie}
                            </span>
                        ) : (
                            <span className="cennik-przezn cennik-przezn--general">
                                <i className="pi pi-globe" />
                                ogólna
                            </span>
                        )
                    }
                />
                <Column
                    header="Cena"
                    sortable
                    sortField="cena"
                    body={(row) =>
                        editingRow === row.id ? (
                            <InputNumber
                                value={editedPrice}
                                onValueChange={(e) => setEditedPrice(e.value)}
                                mode="currency"
                                currency="PLN"
                                locale="pl-PL"
                                min={0}
                            />
                        ) : (
                            `${row.cena} zł`
                        )
                    }
                    style={{ width: "10em" }}
                />
                <Column
                    header="Akcje"
                    body={(row) =>
                        editingRow === row.id ? (
                            <Button
                                icon="pi pi-check"
                                className="p-button-success p-button-sm"
                                onClick={() => updateEntry(row.id)}
                            />
                        ) : (
                            <Button
                                icon="pi pi-pencil"
                                className="p-button-text p-button-sm"
                                onClick={() => {
                                    setEditingRow(row.id);
                                    setEditedPrice(row.cena);
                                }}
                            />
                        )
                    }
                    style={{ width: "5em" }}
                />
                <Column
                    header="Usuń"
                    body={(row) => (
                        <Button
                            icon="pi pi-trash"
                            className="p-button-danger p-button-sm"
                            onClick={() => deleteEntry(row.id)}
                        />
                    )}
                    style={{ width: "5em" }}
                />
            </DataTable>
        );
    };

    return (
        <div>
            <Toast ref={toast} />
                <div className="add-form">
                <h3>Dodaj pozycję do cennika</h3>
                <div className="add-input">
                    <label>Kontrahent</label>
                    <Dropdown
                        value={selectedKontrahent}
                        options={kontrahenci}
                        onChange={(e) => setSelectedKontrahent(e.value)}
                        optionLabel="nazwa"
                        className="wz-dropdown1"
                        placeholder="Wybierz kontrahenta"
                    />
                </div>
                <div className="add-input">
                    <label>Materiał</label>
                    <Dropdown
                        value={selectedMaterial}
                        options={materials}
                        className="wz-dropdown1"
                        onChange={(e) => setSelectedMaterial(e.value)}
                        optionLabel="nazwa"
                        placeholder="Wybierz materiał"
                    />
                </div>
                <div className="add-input">
                    <label>Przeznaczenie (opcjonalnie)</label>
                    <Dropdown
                        value={selectedPrzeznaczenie}
                        options={przeznaczenia}
                        onChange={(e) => setSelectedPrzeznaczenie(e.value)}
                        optionLabel="nazwa"
                        className="wz-dropdown1"
                        placeholder="Cena ogólna kontrahenta"
                        showClear
                    />
                </div>
                <div className="add-input">
                    <label>Cena</label>
                    <InputNumber
                        value={price}
                        onValueChange={(e) => setPrice(e.value)}
                        min={0}
                        mode="currency"
                        currency="PLN"
                        locale="pl-PL"
                    />
                </div>
                <div className="p-col-12">
                    <Button
                        label="Dodaj"
                        icon="pi pi-plus"
                        onClick={handleAdd}
                        disabled={!selectedMaterial || !selectedKontrahent || price == null}
                    />
                </div>
            </div>

            <DataTable
                value={groupedData}
                expandedRows={expandedRows}
                onRowToggle={(e) => setExpandedRows(e.data)}
                rowExpansionTemplate={rowExpansionTemplate}
                dataKey="kontrahent.id"
                sortMode="multiple"
                removableSort
            >
                <Column expander style={{ width: "3em" }} />
                <Column field="kontrahent.nazwa" header="Kontrahent" sortable />
                <Column field="kontrahent.adres" header="Adres" sortable />
                <Column field="kontrahent.nip" header="NIP" sortable />
                <Column field="kontrahent.email" header="Email" sortable />
            </DataTable>
        </div>
    );
};

export default PriceListManager;
